import { DeviceWithInstalledPower, LineChart, ProductTable, SpotPriceResult } from '@e2m-customer-portal/domains';
import { ForecastSlice, MarketLocation, SpotPriceForecast } from '@e2m-customer-portal/hooks';
import { convertMeasure } from '@e2m-customer-portal/shared';
import { DowntimeButton, ErrorComponent } from '@e2m-customer-portal/ui';
import { Button } from '@mui/material';
import clsx from 'clsx';
import { Interval, isPast, toDate } from 'date-fns';
import { ForecastProperty, Resolution } from 'generated/graphql';
import { ReactElement, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { ChartIcon, TableIcon } from '../../../../../../assets/icons';
import { getEarliestMarketingDeadline, isAfterDeadline } from '../../../../../../utils';
import { ControlDeviceTableBodyMobile } from './ControlDeviceTableBodyMobile';
import { DeviceTableBodyMobile } from './DeviceTableBodyMobile';
import { ForecastAssistantMobile } from './ForecastAssistantMobile';

export interface TableBodyProps {
  marketLocation: MarketLocation;
  device: DeviceWithInstalledPower;
  interval: Interval;
  slices: readonly ForecastSlice[];
  productTable: ProductTable;
  spotPriceForecast?: SpotPriceForecast;
  spotPrice: (sliceDate: Date) => SpotPriceResult | undefined;
  isFifteenMinutesResolution?: boolean;
  isInSliceSelectionMode?: boolean;
  sliceSelection: Array<number>;
  setSliceSelection: React.Dispatch<React.SetStateAction<number[]>>;
  assistantDate: Date;
  setAssistantDate: React.Dispatch<React.SetStateAction<Date>>;
}

const TableBody = ({
  marketLocation,
  device,
  interval,
  slices,
  productTable,
  spotPriceForecast,
  spotPrice,
  isFifteenMinutesResolution,
  isInSliceSelectionMode,
  sliceSelection,
  setSliceSelection,
  assistantDate,
  setAssistantDate,
}: TableBodyProps) =>
  productTable === 'control-energy' ? (
    <div id="device-table-container" className="snap-x snap-mandatory scroll-pl-28 overflow-x-auto">
      <ControlDeviceTableBodyMobile device={device} interval={interval} slices={slices} />
    </div>
  ) : (
    <ErrorBoundary fallback={<ErrorComponent />}>
      <div
        id="device-table-container"
        className={clsx(
          'snap-x snap-mandatory scroll-pl-28',
          sliceSelection.length ? 'overflow-hidden' : 'overflow-x-auto',
        )}
      >
        <DeviceTableBodyMobile
          marketLocation={marketLocation}
          device={device}
          interval={interval}
          slices={slices}
          spotPriceForecast={spotPriceForecast}
          spotPrice={spotPrice}
          isFifteenMinutesResolution={isFifteenMinutesResolution}
          isInSliceSelectionMode={isInSliceSelectionMode}
          sliceSelection={sliceSelection}
          setSliceSelection={setSliceSelection}
          assistantDate={assistantDate}
          setAssistantDate={setAssistantDate}
        />
      </div>
    </ErrorBoundary>
  );

export interface DeviceTableMobileProps {
  marketLocation: MarketLocation;
  device: DeviceWithInstalledPower;
  interval: Interval;
  slices: readonly ForecastSlice[];
  controlEnergySlices: readonly ForecastSlice[];
  productTable: ProductTable;
  spotPriceForecast?: SpotPriceForecast;
  spotPrice: (sliceDate: Date) => SpotPriceResult | undefined;
  disableAssistant?: boolean;
  currentForecastProperty?: ForecastProperty;
  setSelectedMobileTable: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export function DeviceTableMobile({
  marketLocation,
  device,
  interval,
  slices,
  controlEnergySlices,
  productTable,
  spotPriceForecast,
  spotPrice,
  disableAssistant,
  currentForecastProperty,
  setSelectedMobileTable,
}: Readonly<DeviceTableMobileProps>): ReactElement {
  const [t] = useTranslation();
  const [isTableView, setIsTableView] = useState<boolean>(false);
  const installedPower = device.installedPower && convertMeasure(device.installedPower, 'kW');

  const [isInSliceSelectionMode, setIsInSliceSelectionMode] = useState(false);
  const [sliceSelection, setSliceSelection] = useState<Array<number>>([]);
  const [assistantDate, setAssistantDate] = useState(new Date());

  useEffect(() => {
    if (!isInSliceSelectionMode) setSliceSelection([]);
  }, [isInSliceSelectionMode]);

  return (
    <div
      className="border-0 border-b border-solid border-b-divider py-2 last-of-type:border-b-0"
      data-testid={`device-table-mobile-${device.id}`}
    >
      <div className="flex w-full items-center justify-between border-0 border-b border-solid border-b-divider pb-2">
        <div className="flex items-center">
          <div className="text-md pl-4 font-semibold">
            <div className="flex items-center text-sm md:text-md">{device.externalName || device.name}</div>
          </div>
          {installedPower && (
            <div className="flex w-max items-center ml-1 md:ml-4 border-0 border-l border-divider border-solid">
              <div className="ml-2 flex flex-col whitespace-nowrap">
                <span className="text-xs font-semibold text-grey-300">
                  {t('DEVICE_TABLE_HEADER_INSTALLED_POWER_LABEL')}
                </span>
                <div className="sm:text-md font-bold">
                  <div className="relative flex items-center">
                    {t('DEVICE_TABLE_HEADER_INSTALLED_POWER', { value: installedPower.value })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-0 border-b-divider pr-2">
          <Button
            className="w-max border-green-200 text-xs disabled:border-grey-200 sm:text-sm"
            color="primary"
            data-testid="MobileViewSwitch"
            variant="outlined"
            onClick={(event) => {
              setSelectedMobileTable(isTableView ? undefined : device.id);
              setIsTableView(!isTableView);
              event?.stopPropagation();
            }}
          >
            {isTableView ? (
              <ChartIcon className="min-w-5  fill-green-200" />
            ) : (
              <TableIcon className="min-w-5 fill-green-200" />
            )}
          </Button>

          <DowntimeButton
            marketLocation={marketLocation}
            device={device}
            disabled={!marketLocation.permissions.editForecast || disableAssistant}
          />
        </div>
      </div>
      {isTableView ? (
        <TableBody
          marketLocation={marketLocation}
          device={device}
          interval={interval}
          slices={slices}
          productTable={productTable}
          spotPriceForecast={spotPriceForecast}
          spotPrice={spotPrice}
          isFifteenMinutesResolution={currentForecastProperty?.resolution === Resolution.FIFTEEN_MINUTES}
          isInSliceSelectionMode={isInSliceSelectionMode}
          sliceSelection={sliceSelection}
          setSliceSelection={setSliceSelection}
          assistantDate={assistantDate}
          setAssistantDate={setAssistantDate}
        />
      ) : (
        <ErrorBoundary fallback={<ErrorComponent />}>
          <div className="py-4">
            <LineChart
              device={device}
              interval={interval}
              slices={slices}
              controlEnergySlices={controlEnergySlices}
              marketLocationName={marketLocation.name}
              spotPriceForecast={spotPriceForecast}
              currentForecastProperty={currentForecastProperty}
              isControlEnergyForecast={productTable !== 'production'}
              isMobile={true}
            />
          </div>
        </ErrorBoundary>
      )}
      {!(marketLocation.marketingEnd != null && isPast(marketLocation.marketingEnd)) && (
        <ForecastAssistantMobile
          marketLocation={marketLocation}
          device={device}
          interval={interval}
          isFifteenMinutesResolution={currentForecastProperty?.resolution === Resolution.FIFTEEN_MINUTES}
          visible={isTableView && productTable === 'production'}
          allowSliceSelection={!isAfterDeadline(getEarliestMarketingDeadline(device.deadlines), toDate(interval.end))}
          selectionModeHasBeenToggled={setIsInSliceSelectionMode}
          sliceSelection={sliceSelection}
          assistantDate={assistantDate}
          currentForecastProperty={currentForecastProperty}
        />
      )}
    </div>
  );
}
